/**
 * 리스트 페이지 — 전체 단지 (Concept A · Human / Warm).
 *
 * 시안: wireframes/concept-a-warm/list.html
 *
 * 섹션 순서:
 *   1. StickyHeader (뒤로 + "전체 단지" + 지도 아이콘)
 *   2. 검색바 (단지명·동·지하철)
 *   3. Sticky 필터 row (전체 / 지역 / 면적 / 세대수 / 입주 / 담당자 안내 / 분양예정)
 *   4. 카운트 + 정렬 라벨
 *   5. 가로 카드 리스트 (PropertyCard variant="horizontal")
 *   6. 더 보기 버튼 (명시적 페이지네이션)
 *   7. 비교 FAB (선택 2개 이상 시 등장)
 *   8. (제거 2026-09-26) 하단 고정 InquiryCTA — 목록 공통 전화·카톡 없음
 *
 * 정책 강제:
 *   - 가격 어디에도 노출 안 함 (PriceTag도 호출 X — 카드 본문에 가격 라인 없음)
 *   - 상태 뱃지 미노출, 분양예정 chip만 허용
 *   - 전화 액션은 INQUIRY_PHONE_TEL_LINK
 */

import { propertyPyeongRange } from '../domain/property/pyeong';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useCompare } from '../domain/compare/useCompare';
import { toggleCompare as toggleCompareStore, clearCompare as clearCompareStore } from '../domain/compare/compareStore';
import { useSeoMeta } from '@unhead/react';
import { propertyApi, Property, listFilterApi, ListFilterPreset, ListFilterPresetGroups } from '../services/api';
import { getPropertyUrl, formatMoveInDate } from '../utils/formatters';
import { isUpcomingBadge, formatUnits } from '../domain/property/formatters';
import { appendSoldOutMatches, matchesSearch, searchKey } from '../domain/property/searchMatch';
import { useSearchSuggestions } from '../hooks/useSearchSuggestions';
import { buildBadges } from '../domain/property/badges';
import { buildRegionTree, matchRegion, regionLabel, type RegionSiNode } from '../domain/property/region';
import {
  StickyHeader,
  FilterChip,
  FilterChipRow,
  PropertyCard,
  DesktopNav,
  DesktopPhonePill,
  FilterSidebar,
  BrandLogo,
  ListCardSkeleton,
  ErrorState,
  errorCodeOf,
  type PropertyCardData,
} from '../design-system/components';
import { ListLayout } from '../design-system/layouts';

const PAGE_SIZE = 10;

type SortKey = 'popular' | 'units' | 'latest';
const SORT_LABEL: Record<SortKey, string> = {
  popular: '검색 많은 순',
  units: '세대수 많은 순',
  latest: '최신 등록순',
};

// 지역 필터 2단계 (2026-09-22 대표 지시): 시·도(대구 우선 → 서울…제주) → 시·군·구.
// 목록은 실제 데이터(address) 기준 — domain/property/region.ts. URL ?si=대구&gu=달서구 로 유지.

// 프리셋은 admin 에서 관리 (사용자 피드백 2026-05-19) — /api/list-filters/ 동적 로드.
// 아래 값은 API fetch 실패 시 fallback 으로만 사용. key 는 string 으로 일반화.
const AREA_FALLBACK: ListFilterPreset[] = [
  { id: -1, filter_type: 'area', key: 'sub60',    label: '~ 59㎡',  min_value: 0,   max_value: 59.99, order: 1 },
  { id: -2, filter_type: 'area', key: 'a60to84',  label: '60~84㎡', min_value: 60,  max_value: 84.99, order: 2 },
  { id: -3, filter_type: 'area', key: 'a84to99',  label: '84~99㎡', min_value: 85,  max_value: 99.99, order: 3 },
  { id: -4, filter_type: 'area', key: 'a100plus', label: '100㎡+',  min_value: 100, max_value: 999,   order: 4 },
];
const UNITS_FALLBACK: ListFilterPreset[] = [
  { id: -5, filter_type: 'units', key: 'u300',     label: '~ 299세대',   min_value: 0,    max_value: 299,   order: 1 },
  { id: -6, filter_type: 'units', key: 'u500',     label: '300~499세대', min_value: 300,  max_value: 499,   order: 2 },
  { id: -7, filter_type: 'units', key: 'u1000',    label: '500~999세대', min_value: 500,  max_value: 999,   order: 3 },
  { id: -8, filter_type: 'units', key: 'u1000plus',label: '1,000세대+',  min_value: 1000, max_value: 99999, order: 4 },
];
const MOVE_IN_FALLBACK: ListFilterPreset[] = [
  { id: -9,  filter_type: 'move_in', key: 'this_year', label: '올해 입주', min_value: 0, max_value: 0,  order: 1 },
  { id: -10, filter_type: 'move_in', key: 'next_year', label: '내년 입주', min_value: 1, max_value: 1,  order: 2 },
  { id: -11, filter_type: 'move_in', key: 'after_2yr', label: '2년 후 +',  min_value: 2, max_value: 99, order: 3 },
];
type AreaKey = string;
type UnitsKey = string;
type MoveInKey = string;

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────
const buildImageUrl = (raw?: string | null): string | null => {
  if (!raw) return null;
  if (raw.startsWith('http') || raw.startsWith('/')) return raw;
  return `/media/${raw}`;
};

const buildLocationLabel = (p: Property): string => {
  const addr = p.address ?? '';
  const cleaned = addr.replace(/^대구광역시\s*/, '').replace(/^경상북도\s*/, '').replace(/^경북\s*/, '');
  return cleaned.split(/\s+/).slice(0, 2).join(' ');
};

const buildMetaLine = (p: Property): string | undefined => {
  const minA = p.min_area || 0;
  const maxA = p.max_area || minA;
  let areaStr = '';
  if (minA && maxA && minA !== maxA) areaStr = `전용 ${minA}~${maxA}㎡`;
  else if (minA) areaStr = `전용 ${minA}㎡`;

  const moveIn = formatMoveInDate(p.move_in_date) || (p.move_in_date ? String(p.move_in_date) : '');
  // 2026-09-26: 평수를 앞에 (공급 기준, 없으면 '전용 N평')
  const py = propertyPyeongRange(p);
  const pieces = [py && !py.startsWith('전용') ? py : '', areaStr, moveIn ? `입주 ${moveIn}` : ''].filter(Boolean);
  return pieces.length ? pieces.join(' · ') : undefined;
};

// admin 관리 preset 기반 매칭. find 콜백에서 동적으로 preset 받음.
const matchArea = (p: Property, key: AreaKey, presets: ListFilterPreset[]): boolean => {
  if (!key) return true;
  const preset = presets.find((a) => a.key === key);
  if (!preset) return true;
  const minA = p.min_area || 0;
  const maxA = p.max_area || minA;
  if (!minA && !maxA) return false;
  return minA <= (preset.max_value ?? Infinity) && maxA >= (preset.min_value ?? 0);
};

const matchUnits = (p: Property, key: UnitsKey, presets: ListFilterPreset[]): boolean => {
  if (!key) return true;
  const preset = presets.find((u) => u.key === key);
  if (!preset) return true;
  const units = p.total_units || 0;
  if (!units) return false;
  return units >= (preset.min_value ?? 0) && units <= (preset.max_value ?? Infinity);
};

const matchMoveIn = (p: Property, key: MoveInKey, presets: ListFilterPreset[]): boolean => {
  if (!key) return true;
  const preset = presets.find((m) => m.key === key);
  if (!preset) return true;
  const m = formatMoveInDate(p.move_in_date) || String(p.move_in_date || '');
  const yearMatch = m.match(/(20\d{2})/);
  if (!yearMatch) return false;
  const year = parseInt(yearMatch[1], 10);
  const now = new Date().getFullYear();
  // year offset 비교 (preset.min_value/max_value 는 연도 offset)
  const offset = year - now;
  return offset >= (preset.min_value ?? 0) && offset <= (preset.max_value ?? Infinity);
};

const isSparseProperty = (p: Property): boolean => {
  // 자료 정리 중 케이스: 이미지·세대수·입주일·면적이 모두 비어있을 때
  const hasImg = !!(p.image_card_url || p.image_thumbnail_url || p.image_url);
  const hasUnits = !!(p.total_units && p.total_units > 0);
  const hasArea = !!((p.min_area || 0) > 0 || (p.max_area || 0) > 0);
  const hasMoveIn = !!p.move_in_date;
  // 3개 이상 비면 sparse
  const missing = [hasImg, hasUnits, hasArea, hasMoveIn].filter((v) => !v).length;
  return missing >= 3;
};

const buildBenefitChips = (p: Property): string[] => {
  const chips: string[] = [];
  if ((p as any).special_marker_text) chips.push((p as any).special_marker_text);
  // "담당자 안내" chip 폐기 (사용자 피드백 2026-05-19)
  return chips;
};

const buildTopLabel = (p: Property): { label?: string; tone: 'accent' | 'muted' } => {
  if (isSparseProperty(p)) return { label: '자료 정리 중', tone: 'muted' };
  // '분양예정'은 v2 배지(buildBadges)로 표시 — topLabel 중복 제거 (2026-09-21)
  const sv = p.naver_search_volume;
  if (typeof sv === 'number' && sv > 0) {
    return { label: `검색량:${sv.toLocaleString()}건`, tone: 'accent' };
  }
  return { label: undefined, tone: 'accent' };
};

const toCardData = (p: Property, top: ReturnType<typeof buildTopLabel>): PropertyCardData => {
  const sparse = isSparseProperty(p);
  return {
    id: p.id,
    title: p.title,
    locationLabel:
      buildLocationLabel(p) +
      (p.total_units && p.total_units > 0 ? ` · ${formatUnits(p, { short: true })}` : ' · 세대수 미정'),
    imageUrl: buildImageUrl(p.image_card_url || p.image_thumbnail_url || p.image_url),
    isPreRegistration: isUpcomingBadge(p.status, (p as any).is_pre_registration === true),
    isSparse: sparse,
    metaLine: buildMetaLine(p) ?? (sparse ? '전용 미정 · 입주 미정' : undefined),
    benefitChips: sparse ? ['자료 추가 중'] : buildBenefitChips(p),
    // v2: 대표혜택 → 할인율 → 분양예정 (최대 3). 분양완료는 이름 검색으로만 나오며 맨 앞에 '분양완료'(2026-10-01)
    badges:
      p.status === '분양완료'
        ? [{ key: 'soldout', label: '분양완료', tone: 'mute', ariaLabel: '분양 상태' } as const, ...buildBadges(p)].slice(0, 3)
        : buildBadges(p),
    topLabel: top.label,
    topLabelTone: top.tone,
  };
};

// ─────────────────────────────────────────────
// Icons
// ─────────────────────────────────────────────
const SearchIcon: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
  </svg>
);
const BackIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <polyline points="15 18 9 12 15 6" />
  </svg>
);
const MapIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" />
    <line x1="8" y1="2" x2="8" y2="18" /><line x1="16" y1="6" x2="16" y2="22" />
  </svg>
);
const ChevronDownIcon: React.FC<{ size?: number }> = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden>
    <polyline points="6 9 12 15 18 9" />
  </svg>
);
const CompareIcon: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
  </svg>
);
const XIcon: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" aria-hidden>
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

// ─────────────────────────────────────────────
// Filter sheets — 지역 / 면적 / 세대수 / 입주
// ─────────────────────────────────────────────
type FilterKind = 'region' | 'area' | 'units' | 'movein' | null;

interface FilterSheetProps {
  open: boolean;
  onClose: () => void;
  title: string;
  options: { key: string; label: string }[];
  selected: string;
  onSelect: (key: string) => void;
}

const FilterSheet: React.FC<FilterSheetProps> = ({ open, onClose, title, options, selected, onSelect }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end bg-black/40" onClick={onClose}>
      <div
        className="w-full rounded-t-ca-4xl bg-frame pb-4 pt-2 shadow-ca-sheet"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mx-auto mt-1 mb-2 h-1 w-9 rounded-full bg-mute3" />
        <div className="mb-2 flex items-center justify-between px-4">
          <h3 className="text-[15px] font-extrabold text-ink tracking-tight">{title}</h3>
          <button type="button" onClick={onClose} className="rounded-ca-md p-1.5 text-muted" aria-label="닫기">
            <XIcon size={18} />
          </button>
        </div>
        <div className="grid grid-cols-2 gap-2 px-4 pb-2">
          <button
            type="button"
            onClick={() => { onSelect(''); onClose(); }}
            className={`rounded-ca-xl border px-3 py-3 text-[13px] font-bold ${
              selected === '' ? 'border-accent bg-accent-50 text-accent-dark' : 'border-ca-line bg-frame text-ink'
            }`}
          >
            전체
          </button>
          {options.map((o) => (
            <button
              key={o.key}
              type="button"
              onClick={() => { onSelect(o.key); onClose(); }}
              className={`rounded-ca-xl border px-3 py-3 text-[13px] font-bold ${
                selected === o.key ? 'border-accent bg-accent-50 text-accent-dark' : 'border-ca-line bg-frame text-ink'
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// Region sheet — 모바일 2단계 (시·도 → 시·군·구)
//   시·도 탭 = 즉시 그 시·도 전체 적용 + 아래에 시·군·구 목록 / 시·군·구 탭 = 적용 후 닫기
// ─────────────────────────────────────────────
const RegionSheet: React.FC<{
  open: boolean;
  onClose: () => void;
  tree: RegionSiNode[];
  si: string;
  gu: string;
  onChange: (si: string, gu: string) => void;
}> = ({ open, onClose, tree, si, gu, onChange }) => {
  if (!open) return null;
  const node = tree.find((n) => n.si === si);
  const optCls = (active: boolean) =>
    `rounded-ca-xl border px-2 py-2.5 text-[13px] font-bold ${
      active ? 'border-accent bg-accent-50 text-accent-dark' : 'border-ca-line bg-frame text-ink'
    }`;
  const Count: React.FC<{ n: number }> = ({ n }) => (
    <span className="num ml-1 text-[11px] font-medium text-mute2">{n.toLocaleString()}</span>
  );
  return (
    <div className="fixed inset-0 z-50 flex items-end bg-black/40" onClick={onClose}>
      <div
        className="flex max-h-[85vh] w-full flex-col rounded-t-ca-4xl bg-frame pb-4 pt-2 shadow-ca-sheet"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="지역 선택"
      >
        <div className="mx-auto mt-1 mb-2 h-1 w-9 shrink-0 rounded-full bg-mute3" />
        <div className="mb-2 flex shrink-0 items-center justify-between px-4">
          <h3 className="text-[15px] font-extrabold text-ink tracking-tight">지역</h3>
          <button type="button" onClick={onClose} className="rounded-ca-md p-1.5 text-muted" aria-label="닫기">
            <XIcon size={18} />
          </button>
        </div>
        <div className="overflow-y-auto px-4 pb-2">
          <h4 className="mb-2 text-[12px] font-bold text-muted">시·도</h4>
          <div className="grid grid-cols-4 gap-2">
            <button type="button" onClick={() => { onChange('', ''); onClose(); }} className={optCls(si === '')}>
              전체
            </button>
            {tree.map((n) => (
              <button
                key={n.si}
                type="button"
                onClick={() => onChange(n.si, '')}
                className={optCls(si === n.si)}
                aria-pressed={si === n.si}
              >
                {n.si}<Count n={n.count} />
              </button>
            ))}
          </div>
          {node && node.gus.length > 0 && (
            <>
              <h4 className="mb-2 mt-4 text-[12px] font-bold text-muted">{node.si} 시·군·구</h4>
              <div className="grid grid-cols-3 gap-2">
                <button type="button" onClick={() => { onChange(node.si, ''); onClose(); }} className={optCls(gu === '')}>
                  {node.si} 전체<Count n={node.count} />
                </button>
                {node.gus.map((g) => (
                  <button
                    key={g.gu}
                    type="button"
                    onClick={() => { onChange(node.si, g.gu); onClose(); }}
                    className={optCls(gu === g.gu)}
                    aria-pressed={gu === g.gu}
                  >
                    {g.gu}<Count n={g.count} />
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// Sort sheet
// ─────────────────────────────────────────────
const SortSheet: React.FC<{
  open: boolean;
  onClose: () => void;
  current: SortKey;
  onSelect: (k: SortKey) => void;
}> = ({ open, onClose, current, onSelect }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end bg-black/40" onClick={onClose}>
      <div
        className="w-full rounded-t-ca-4xl bg-frame pb-4 pt-2 shadow-ca-sheet"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mx-auto mt-1 mb-2 h-1 w-9 rounded-full bg-mute3" />
        <h3 className="mb-1 px-4 text-[15px] font-extrabold text-ink tracking-tight">정렬</h3>
        {(Object.keys(SORT_LABEL) as SortKey[]).map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => { onSelect(k); onClose(); }}
            className={`flex w-full items-center justify-between px-4 py-3.5 text-left text-[14px] ${
              current === k ? 'font-extrabold text-accent-dark' : 'text-ink'
            }`}
          >
            {SORT_LABEL[k]}
            {current === k && <span className="text-accent">✓</span>}
          </button>
        ))}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// Compare FAB + sheet
// ─────────────────────────────────────────────
const CompareFab: React.FC<{ count: number; onClick: () => void }> = ({ count, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="fixed bottom-[80px] right-3.5 z-20 flex items-center gap-1.5 rounded-full bg-ink px-4 py-2.5 text-[13px] font-extrabold tracking-tight text-white md:bottom-6"
    style={{ boxShadow: '0 12px 28px rgba(15,23,42,.25)' }}
    aria-label={`비교 ${count}개`}
  >
    <CompareIcon />
    비교
    <span className="rounded-full bg-accent px-1.5 py-px text-[11px] font-bold tabular-nums">{count}</span>
  </button>
);

// ─────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────
const ListPage: React.FC = () => {
  const navigate = useNavigate();

  const [properties, setProperties] = useState<Property[]>([]);

  const [soldOut, setSoldOut] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  // v2 S3 — 목록 로드 실패 (오류코드·시각)
  const [loadError, setLoadError] = useState<{ code: string; at: Date } | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  // admin 관리 필터 프리셋 (사용자 피드백 2026-05-19) — API 응답 우선, 실패 시 fallback
  const [areaPresets, setAreaPresets] = useState<ListFilterPreset[]>(AREA_FALLBACK);
  const [unitsPresets, setUnitsPresets] = useState<ListFilterPreset[]>(UNITS_FALLBACK);
  const [moveInPresets, setMoveInPresets] = useState<ListFilterPreset[]>(MOVE_IN_FALLBACK);

  useEffect(() => {
    let cancelled = false;
    listFilterApi.groups().then((g: ListFilterPresetGroups) => {
      if (cancelled) return;
      if (g.area && g.area.length > 0) setAreaPresets(g.area);
      if (g.units && g.units.length > 0) setUnitsPresets(g.units);
      if (g.move_in && g.move_in.length > 0) setMoveInPresets(g.move_in);
    }).catch(() => {
      // 실패 시 fallback 그대로 사용
    });
    return () => { cancelled = true; };
  }, []);

  // 검색 — GNB 검색(/list?search=…) 진입 시 초기값 (v2 2026-09-21)
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState(
    () => new URLSearchParams(location.search).get('search') ?? '',
  );
  const suggestedIds = useSearchSuggestions(searchQuery);

  // 4축 + 토글 필터
  // 지역 2단계 — URL ?si=&gu= 가 단일 소스 (새로고침·공유 시 유지)
  const regionParams = new URLSearchParams(location.search);
  const regionSi = regionParams.get('si') ?? '';
  const regionGu = regionSi ? (regionParams.get('gu') ?? '') : '';
  const region = regionLabel(regionSi, regionGu);
  const setRegion = useCallback((si: string, gu: string) => {
    const params = new URLSearchParams(location.search);
    if (si) params.set('si', si); else params.delete('si');
    if (si && gu) params.set('gu', gu); else params.delete('gu');
    const qs = params.toString();
    navigate({ pathname: location.pathname, search: qs ? `?${qs}` : '' }, { replace: true });
  }, [location.pathname, location.search, navigate]);
  const [area, setArea] = useState<AreaKey>('');
  const [units, setUnits] = useState<UnitsKey>('');
  const [moveIn, setMoveIn] = useState<MoveInKey>('');
  const [onlyPreRegistration, setOnlyPreRegistration] = useState(false);

  // 정렬
  const [sort, setSort] = useState<SortKey>('popular');

  // sheets
  const [openSheet, setOpenSheet] = useState<FilterKind>(null);
  const [openSort, setOpenSort] = useState(false);

  // 페이지네이션
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  // 비교
  // 2026-09-25: 이 화면만의 임시 목록 → 공용 비교함(localStorage). 지도·/compare 와 같은 목록을 쓴다.
  //   전엔 목록에서 고른 단지가 비교표(/compare)로 이어지지 않아 모바일에서 '비교'가 되지 않았다.
  const compareList = useCompare();
  const compareIds = useMemo(() => new Set<number | string>(compareList), [compareList]);
  const [compareMode, setCompareMode] = useState(false);
  const [compareSheetOpen, setCompareSheetOpen] = useState(false);

  // 검색 입력 ref (모바일 자동완성 트리거용)
  const searchRef = useRef<HTMLInputElement>(null);

  // 데이터 로드
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    (async () => {
      try {
        const data = await propertyApi.getMapData();
        if (cancelled) return;
        setProperties(data);
        // 분양완료 현장은 이름 검색 때만 쓴다 — 실패해도 목록은 정상
        propertyApi.getSoldOutMapData().then((l) => { if (!cancelled) setSoldOut(l); }).catch(() => {});
      } catch (e) {
        console.error('Failed to load properties:', e);
        if (!cancelled) setLoadError({ code: errorCodeOf(e), at: new Date() });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  // 검색 + 필터 + 정렬
  // 지역을 뺀 나머지 필터 결과 — 지역 옵션 카운트 기준
  const filteredExceptRegion = useMemo(() => {
    let list = [...properties];

    // 검색
    if (searchKey(searchQuery)) {
      // 서버 토큰 매칭(순서 바뀜·동의어) ∪ 로컬 매칭(띄어쓰기 무시). 2026-09-24 대표 제보 대응.
      const suggested = new Set(suggestedIds ?? []);
      list = list.filter((p) => suggested.has(Number(p.id)) || matchesSearch(p, searchQuery));
      list = appendSoldOutMatches(list, soldOut, searchQuery, suggestedIds);
    }
    if (area) list = list.filter((p) => matchArea(p, area, areaPresets));
    if (units) list = list.filter((p) => matchUnits(p, units, unitsPresets));
    if (moveIn) list = list.filter((p) => matchMoveIn(p, moveIn, moveInPresets));
    if (onlyPreRegistration) list = list.filter((p) => isUpcomingBadge(p.status, (p as any).is_pre_registration === true));
    return list;
  }, [properties, soldOut, searchQuery, suggestedIds, area, units, moveIn, onlyPreRegistration, areaPresets, unitsPresets, moveInPresets]);

  const regionTree = useMemo(
    () => buildRegionTree(properties, filteredExceptRegion),
    [properties, filteredExceptRegion],
  );
  const regionNode = regionTree.find((n) => n.si === regionSi);

  const filtered = useMemo(() => {
    const list = filteredExceptRegion.filter((p) => matchRegion(p.address, regionSi, regionGu));

    // 정렬
    if (sort === 'popular') {
      list.sort((a, b) => {
        const sa = a.naver_search_volume || 0;
        const sb = b.naver_search_volume || 0;
        if (sa !== sb) return sb - sa;
        // tie-break: main_display_order → id desc
        const ma = (a as any).main_display_order || 9999;
        const mb = (b as any).main_display_order || 9999;
        if (ma !== mb) return ma - mb;
        return b.id - a.id;
      });
    } else if (sort === 'units') {
      list.sort((a, b) => (b.total_units || 0) - (a.total_units || 0));
    } else {
      // latest
      list.sort((a, b) => b.id - a.id);
    }
    // 검색 중이면 이름이 직접 맞는 현장을 정렬과 무관하게 맨 위로 — 서버 자동완성의 느슨한 결과('자이' 포함 타지역)가
    // 앞을 차지해 찾는 현장이 맨 뒤로 밀리던 문제(2026-10-01 '대명자이'). sort 는 안정 정렬이라 각 묶음 안 순서는 유지.
    if (searchKey(searchQuery)) {
      list.sort((a, b) => Number(matchesSearch(b, searchQuery)) - Number(matchesSearch(a, searchQuery)));
    }
    return list;
  }, [filteredExceptRegion, regionSi, regionGu, sort, searchQuery]);

  const displayed = filtered.slice(0, visibleCount);
  const remaining = Math.max(0, filtered.length - visibleCount);

  // 필터 변경 시 페이지 리셋
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [searchQuery, region, area, units, moveIn, onlyPreRegistration, sort]);

  const toggleCompare = useCallback((id: number | string) => {
    toggleCompareStore(Number(id)); // 정원(4개) 초과면 담지 않음
  }, []);

  const clearCompare = () => {
    clearCompareStore();
    setCompareMode(false);
    setCompareSheetOpen(false);
  };

  const compareCount = compareIds.size;
  const compareItems = properties.filter((p) => compareIds.has(p.id));

  useSeoMeta({
    title: '전체 단지 | 부냥',
    description: '대구·경북 신규 분양 단지 전체 목록. 지역·면적·세대수·입주일로 필터하고 2~3개 단지를 비교하세요. 분양상담 1533-8124',
  });

  return (
    <>
      <div className="min-h-screen bg-paper text-body">
        <div className="mx-auto max-w-[480px] bg-frame md:max-w-[720px] lg:max-w-[1140px] xl:max-w-[1240px] 2xl:max-w-[1320px]">
          {/* 1. StickyHeader — md+ DesktopNav, lg+ DesktopPhonePill */}
          <StickyHeader
            left={
              <div className="flex items-center gap-6">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="flex items-center gap-1.5 rounded-ca-md px-1 text-[13px] font-semibold text-ink md:hidden"
                  aria-label="뒤로"
                >
                  <BackIcon /> 뒤로
                </button>
                <BrandLogo className="hidden md:flex" />
                <DesktopNav />
              </div>
            }
            center={<span className="md:hidden">전체 단지</span>}
            forceCompact={false}
            right={
              <>
                <DesktopPhonePill className="mr-1" />
                <Link
                  to="/map"
                  className="flex h-[38px] w-[38px] items-center justify-center rounded-ca-md text-muted hover:bg-ca-surface hover:text-ink"
                  aria-label="지도로 보기"
                >
                  <MapIcon />
                </Link>
              </>
            }
          />

          {/* 2. 검색바 */}
          <section className="border-b border-ca-line-soft bg-frame px-3.5 py-3">
            <label className="flex items-center gap-2.5 rounded-ca-xl border border-ca-line bg-ca-surface px-3.5 py-2.5">
              <span className="text-mute2">
                <SearchIcon />
              </span>
              <input
                ref={searchRef}
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="단지명·동·지하철로 검색"
                className="flex-1 bg-transparent text-[13px] text-ink placeholder:text-muted outline-none"
                aria-label="단지 검색"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="text-mute2"
                  aria-label="검색어 지우기"
                >
                  <XIcon size={16} />
                </button>
              )}
            </label>
          </section>

          {/* 3. Sticky 필터 row — 모바일/태블릿 전용 (lg+에서는 좌측 FilterSidebar가 대체) */}
          <div className="sticky top-[54px] z-10 bg-frame lg:hidden">
            <FilterChipRow>
              <FilterChip
                label="전체"
                active={!region && !area && !units && !moveIn && !onlyPreRegistration}
                count={filtered.length}
                onClick={() => {
                  setRegion('', ''); setArea(''); setUnits(''); setMoveIn(''); setOnlyPreRegistration(false);
                }}
              />
              <FilterChip
                label={region || '지역'}
                active={!!region}
                icon={<ChevronDownIcon size={10} />}
                onClick={() => setOpenSheet('region')}
              />
              <FilterChip
                label={area ? (areaPresets.find((a) => a.key === area)?.label ?? '면적') : '면적'}
                active={!!area}
                icon={<ChevronDownIcon size={10} />}
                onClick={() => setOpenSheet('area')}
              />
              <FilterChip
                label={units ? (unitsPresets.find((u) => u.key === units)?.label ?? '세대수') : '세대수'}
                active={!!units}
                icon={<ChevronDownIcon size={10} />}
                onClick={() => setOpenSheet('units')}
              />
              <FilterChip
                label={moveIn ? (moveInPresets.find((m) => m.key === moveIn)?.label ?? '입주') : '입주'}
                active={!!moveIn}
                icon={<ChevronDownIcon size={10} />}
                onClick={() => setOpenSheet('movein')}
              />
              {/* "담당자 안내" 필터칩 폐기 (사용자 피드백 2026-05-19) */}
              <FilterChip
                label="분양예정"
                active={onlyPreRegistration}
                emphasis
                onClick={() => setOnlyPreRegistration((v) => !v)}
              />
            </FilterChipRow>
          </div>

          {/* lg+ 좌측 사이드바 + 카운트/카드/더보기 — ListLayout */}
          <ListLayout
            sidebar={
              <FilterSidebar
                groups={[
                  {
                    title: '지역',
                    selected: regionSi,
                    onSelect: (k) => setRegion(k, ''),
                    options: regionTree.map((n) => ({ key: n.si, label: n.si, count: n.count })),
                  },
                  ...(regionNode && regionNode.gus.length > 0
                    ? [{
                        title: `${regionNode.si} 시·군·구`,
                        selected: regionGu,
                        onSelect: (k: string) => setRegion(regionNode.si, k),
                        allLabel: `${regionNode.si} 전체`,
                        options: regionNode.gus.map((g) => ({ key: g.gu, label: g.gu, count: g.count })),
                      }]
                    : []),
                  {
                    title: '전용 면적',
                    selected: area,
                    onSelect: (k) => setArea(k as AreaKey),
                    options: areaPresets.map((a) => ({ key: a.key, label: a.label })),
                  },
                  {
                    title: '세대수',
                    selected: units,
                    onSelect: (k) => setUnits(k as UnitsKey),
                    options: unitsPresets.map((u) => ({ key: u.key, label: u.label })),
                  },
                  {
                    title: '입주 시기',
                    selected: moveIn,
                    onSelect: (k) => setMoveIn(k as MoveInKey),
                    options: moveInPresets.map((m) => ({ key: m.key, label: m.label })),
                  },
                ]}
                header={
                  <div className="flex flex-col pb-3.5">
                    <div className="flex items-center justify-between">
                      <b className="text-[16px] font-semibold text-ink">필터</b>
                      <button
                        type="button"
                        onClick={() => {
                          setRegion('', ''); setArea(''); setUnits(''); setMoveIn(''); setOnlyPreRegistration(false);
                        }}
                        className="text-[11px] font-bold text-accent"
                      >
                        초기화
                      </button>
                    </div>
                    {/* "담당자 안내" 사이드바 체크박스 폐기 (사용자 피드백 2026-05-19) */}
                    <button
                      type="button"
                      onClick={() => setOnlyPreRegistration((v) => !v)}
                      aria-pressed={onlyPreRegistration}
                      className={`mt-3 flex items-center gap-2 py-[5px] text-left text-[13px] ${onlyPreRegistration ? 'font-semibold text-ink' : 'text-body'}`}
                    >
                      <i
                        aria-hidden
                        className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] border-[1.5px] not-italic ${
                          onlyPreRegistration ? 'border-accent bg-accent text-white' : 'border-ca-line bg-frame'
                        }`}
                      >
                        {onlyPreRegistration && (
                          <svg width={10} height={10} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={4} strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        )}
                      </i>
                      분양예정만
                    </button>
                  </div>
                }
              />
            }
            content={
              <>
                {/* 4. 카운트 + 정렬 */}
                <section className="flex items-center justify-between border-b border-ca-line-soft bg-frame px-3.5 py-2.5 text-[12px] lg:border-0 lg:bg-transparent lg:px-0 lg:pb-3.5 lg:pt-0 lg:text-[13px]">
                  <div className="text-muted">
                    <b className="font-extrabold tabular-nums text-ink">
                      {filtered.length.toLocaleString()}개
                    </b>{' '}
                    단지 · {region || '전체 지역'}
                  </div>
                  <button
                    type="button"
                    onClick={() => setOpenSort(true)}
                    className="flex items-center gap-1 font-bold text-ink"
                  >
                    {SORT_LABEL[sort]} <ChevronDownIcon />
                  </button>
                </section>

                {/* 5. 카드 리스트 — 모바일 1열, md+ 2열, 2xl 3열 */}
                <section className="grid grid-cols-1 gap-3.5 bg-frame p-3.5 md:grid-cols-2 md:gap-4 md:p-6 lg:bg-transparent lg:p-0 xl:grid-cols-3">
                  {loading ? (
                    // v2 S1 — 스켈레톤 5개 shimmer 1.4s (스피너·pulse 금지)
                    Array.from({ length: 5 }).map((_, i) => <ListCardSkeleton key={i} />)
                  ) : loadError ? (
                    <div className="md:col-span-2 xl:col-span-3">
                      <ErrorState onRetry={() => setReloadKey((k) => k + 1)} code={loadError.code} at={loadError.at} />
                    </div>
                  ) : displayed.length === 0 ? (
                    <div className="rounded-ca-2xl border border-ca-line bg-ca-surface py-10 text-center text-[13px] text-muted md:col-span-2 xl:col-span-3">
                      조건에 맞는 단지가 없어요. 필터를 다시 시도해보세요.
                    </div>
                  ) : (
                    displayed.map((p) => {
                      const top = buildTopLabel(p);
                      const data = toCardData(p, top);
                      const selected = compareIds.has(p.id);
                      const handleCardClick = () => navigate(getPropertyUrl(p));
                      return (
                        <PropertyCard
                          key={p.id}
                          data={data}
                          variant="horizontal"
                          onClick={handleCardClick}
                          selectable={compareMode}
                          selected={selected}
                          onToggleSelect={() => toggleCompare(p.id)}
                        />
                      );
                    })
                  )}
                </section>

                {/* 6. 더 보기 */}
                {!loading && remaining > 0 && (
                  <div className="bg-frame px-3.5 pb-6 lg:bg-transparent lg:px-0 lg:pb-8 lg:pt-5">
                    <button
                      type="button"
                      onClick={() => setVisibleCount((v) => v + PAGE_SIZE)}
                      className="flex w-full items-center justify-center gap-1.5 rounded-ca-xl border border-ca-line-strong bg-frame px-3 py-3.5 text-[13px] font-extrabold tracking-tight text-ink"
                    >
                      더 보기 ({remaining.toLocaleString()}개 남음)
                      <ChevronDownIcon size={14} />
                    </button>
                  </div>
                )}
              </>
            }
          />

          {/* 8. (2026-09-26 제거) 하단 고정 상담 CTA — 대표: 특별 현장 외 담당자 없음, 목록 공통 전화·카톡도 빼라 */}
        </div>

        {/* 7. 비교 FAB — 모바일은 하단 탭바(64px) 위 80px, md 이상 24px
            (2026-09-26 하단 상담 바 제거로 144px→80px) */}
        {compareCount === 0 && !compareMode ? (
          <button
            type="button"
            onClick={() => setCompareMode(true)}
            className="fixed bottom-[80px] right-3.5 z-20 flex items-center gap-1.5 rounded-full bg-ink px-4 py-2.5 text-[13px] font-extrabold tracking-tight text-white md:bottom-6"
            style={{ boxShadow: '0 12px 28px rgba(15,23,42,.25)' }}
            aria-label="비교 모드 시작"
          >
            <CompareIcon /> 비교 시작
          </button>
        ) : (
          <CompareFab count={compareCount} onClick={() => setCompareSheetOpen(true)} />
        )}

        {/* 필터 시트들 */}
        <RegionSheet
          open={openSheet === 'region'}
          onClose={() => setOpenSheet(null)}
          tree={regionTree}
          si={regionSi}
          gu={regionGu}
          onChange={setRegion}
        />
        <FilterSheet
          open={openSheet === 'area'}
          onClose={() => setOpenSheet(null)}
          title="전용 면적"
          options={areaPresets.map((a) => ({ key: a.key, label: a.label }))}
          selected={area}
          onSelect={(k) => setArea(k as AreaKey)}
        />
        <FilterSheet
          open={openSheet === 'units'}
          onClose={() => setOpenSheet(null)}
          title="세대수"
          options={unitsPresets.map((u) => ({ key: u.key, label: u.label }))}
          selected={units}
          onSelect={(k) => setUnits(k as UnitsKey)}
        />
        <FilterSheet
          open={openSheet === 'movein'}
          onClose={() => setOpenSheet(null)}
          title="입주 시기"
          options={moveInPresets.map((m) => ({ key: m.key, label: m.label }))}
          selected={moveIn}
          onSelect={(k) => setMoveIn(k as MoveInKey)}
        />

        {/* 정렬 시트 */}
        <SortSheet
          open={openSort}
          onClose={() => setOpenSort(false)}
          current={sort}
          onSelect={(k) => setSort(k)}
        />

        {/* 비교 시트 */}
        {compareSheetOpen && (
          <div className="fixed inset-0 z-50 flex items-end bg-black/40" onClick={() => setCompareSheetOpen(false)}>
            <div
              className="w-full rounded-t-ca-4xl bg-frame pb-6 pt-2 shadow-ca-sheet"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mx-auto mt-1 mb-2 h-1 w-9 rounded-full bg-mute3" />
              <div className="flex items-center justify-between px-4 pb-2">
                <h3 className="text-[15px] font-extrabold tracking-tight text-ink">
                  비교 {compareCount}개
                </h3>
                <button type="button" onClick={clearCompare} className="text-[12px] font-bold text-muted">
                  모두 지우기
                </button>
              </div>
              <div className="px-3 pb-3">
                {compareItems.length === 0 ? (
                  <p className="px-1 py-6 text-center text-[13px] text-muted">
                    카드의 우상단 체크박스를 눌러 단지를 선택하세요.
                  </p>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    {compareItems.map((p) => (
                      <div key={p.id} className="relative">
                        <Link to={getPropertyUrl(p)} className="block overflow-hidden rounded-ca-2xl border border-ca-line">
                          <div className="aspect-[5/3] bg-block">
                            {buildImageUrl(p.image_card_url || p.image_thumbnail_url || p.image_url) && (
                              <img
                                src={buildImageUrl(p.image_card_url || p.image_thumbnail_url || p.image_url) ?? ''}
                                alt={p.title}
                                className="h-full w-full object-cover"
                                loading="lazy"
                              />
                            )}
                          </div>
                          <div className="p-2">
                            <p className="text-[12px] font-extrabold tracking-tight text-ink line-clamp-1">{p.title}</p>
                            <p className="mt-0.5 text-[10.5px] text-muted">{buildLocationLabel(p)}</p>
                          </div>
                        </Link>
                        <button
                          type="button"
                          onClick={() => toggleCompare(p.id)}
                          className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-ink/70 text-white"
                          aria-label="비교에서 제거"
                        >
                          <XIcon size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {/* 비교 액션 — 상세 비교표(/compare)로 이동 (2026-09-25), 아래 상담 전화 유지 */}
              <div className="flex flex-col gap-2 px-3.5">
                <button
                  type="button"
                  disabled={compareCount < 2}
                  onClick={() => navigate('/compare')}
                  className="w-full rounded-ca-lg bg-accent py-3 text-[14px] font-extrabold text-white disabled:bg-mute3 disabled:text-muted"
                >
                  {compareCount < 2 ? '2개 이상 담으면 상세 비교할 수 있어요' : `상세 비교하기 (${compareCount}개)`}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </>
  );
};

export default ListPage;
